/**
 * LibraryToolEdit — create / edit a tool definition.
 *
 * Two modes:
 *   - Form: sectioned cards for every field of the tool manifest
 *   - JSON: raw JSON editor with live validation + copy button
 *
 * The two modes round-trip through the same state object, so switching
 * between them preserves edits as long as the JSON is valid.
 *
 * Builtin tools are read-only (seeded, not user-editable) — the page
 * shows their configuration but disables all edits.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
} from "../components.jsx";
import { useLibraryEnv, useEnvPrefix } from "../LibraryEnvContext.jsx";
import { KindBadge } from "./LibraryTools.jsx";

// ─── Defaults ────────────────────────────────────────────────────────
const COMMON_DEFAULTS = {
  name: "",
  description: "",
  category: "",
  timeout_seconds: 20,
  runtime_variables: { declared: [], defaults: {} },
  result_bindings: [],
  blocks_barge_in: false,
  pre_call_narration: "auto",
  progress_sound: null,
  progress_sound_mode: "auto",
  dispatch: "immediate",
  on_error: "auto",
  stub_responses: [],
  tags: [],
  system_tool: false,
};

function emptyServerTool(id = "") {
  return {
    id,
    kind: "server",
    ...COMMON_DEFAULTS,
    request: {
      url: "",
      method: "GET",
      path_params: [],
      query_params: [],
      body_params: [],
      headers: [],
      content_type: "application/json",
      auth_ref: null,
    },
  };
}

function emptyClientTool(id = "") {
  return {
    id,
    kind: "client",
    ...COMMON_DEFAULTS,
    awaits_reply: false,
    timeout_seconds: 1,
    parameters: [],
  };
}

const NARRATION_OPTIONS = ["auto", "always", "never"];
const DISPATCH_OPTIONS = ["immediate", "queued", "after_speech"];
const ON_ERROR_OPTIONS = ["auto", "retry", "fail_open", "fail_closed"];
const PROGRESS_SOUND_MODES = ["auto", "always", "never"];
const PARAM_TYPES = ["string", "integer", "number", "boolean", "object", "array"];
const PARAM_SOURCES = [
  { value: "llm", label: "LLM-provided" },
  { value: "runtime_variable", label: "Runtime variable" },
  { value: "constant", label: "Constant" },
];

// ─── Main component ──────────────────────────────────────────────────
export default function LibraryToolEdit() {
  const nav = useNavigate();
  const loc = useLocation();
  const { id } = useParams();
  const { isLive } = useLibraryEnv();
  const prefix = useEnvPrefix();

  const isNew = !id;
  const queryKind =
    new URLSearchParams(loc.search).get("kind") || "server";

  const [tool, setTool] = useState(() =>
    isNew
      ? queryKind === "client"
        ? emptyClientTool()
        : emptyServerTool()
      : null
  );
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("form"); // "form" | "json"
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState(null);

  const readOnly = isLive || tool?.kind === "builtin";

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await api.getTool(id);
      setTool(res.data);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    load();
  }, [load]);

  // When switching to JSON mode, serialize the current state.
  useEffect(() => {
    if (mode === "json" && tool) {
      setJsonText(JSON.stringify(tool, null, 2));
      setJsonError(null);
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSwitchToForm = () => {
    // Parse the JSON back into state. If it fails, stay in JSON mode.
    try {
      const parsed = JSON.parse(jsonText);
      setTool(parsed);
      setJsonError(null);
      setMode("form");
    } catch (err) {
      setJsonError(err.message);
    }
  };

  const handleSave = async () => {
    let payload;
    if (mode === "json") {
      try {
        payload = JSON.parse(jsonText);
      } catch (err) {
        setJsonError(err.message);
        return;
      }
    } else {
      payload = tool;
    }

    if (!payload.id && !payload._id) {
      setError(new Error("id is required"));
      return;
    }
    if (!payload.name?.trim()) {
      setError(new Error("name is required"));
      return;
    }
    if (!payload.kind) {
      setError(new Error("kind is required (server, client, or builtin)"));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const toolId = payload.id || payload._id;
      await api.upsertTool({ ...payload, id: toolId });
      nav(`${prefix}/tools`);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!tool || isNew) return;
    if (
      !window.confirm(
        `Delete tool "${tool.name || tool._id}"?\n\nAgents that reference this tool will still have it in their tools[] list until manually removed.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.deleteTool(tool._id);
      nav(`${prefix}/tools`);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  const updateField = (path, value) => {
    setTool((t) => setPath(t, path, value));
  };

  if (loading) return <Loading />;
  if (!tool) return null;

  const kind = tool.kind;

  return (
    <>
      <PageHeader
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {isNew ? (kind === "client" ? "New client tool" : "New server tool") : tool.name || tool._id}
            <KindBadge kind={kind} />
          </span>
        }
        breadcrumbs={[
          { label: "Library", to: `${prefix}/platform` },
          { label: "Tools", to: `${prefix}/tools` },
          { label: isNew ? "New" : tool.name || tool._id },
        ]}
        actions={
          <div className="btn-row">
            {!isNew && !readOnly && (
              <button
                className="btn danger"
                onClick={handleDelete}
                disabled={busy}
              >
                Delete
              </button>
            )}
            <button
              className="btn"
              onClick={() => nav(`${prefix}/tools`)}
              disabled={busy}
            >
              Cancel
            </button>
            {!readOnly && (
              <button
                className="btn primary"
                onClick={handleSave}
                disabled={busy}
              >
                {busy ? "Saving..." : isNew ? "Create" : "Save"}
              </button>
            )}
          </div>
        }
      />

      <div className="tool-edit-mode-bar">
        <div className="btn-row">
          <button
            className={`btn sm ${mode === "form" ? "primary" : ""}`}
            onClick={() => (mode === "json" ? handleSwitchToForm() : null)}
          >
            Form
          </button>
          <button
            className={`btn sm ${mode === "json" ? "primary" : ""}`}
            onClick={() => setMode("json")}
          >
            JSON
          </button>
        </div>
        {readOnly && (
          <span className="muted small">
            {isLive
              ? "Read-only in Live mode"
              : "Builtin tools are read-only"}
          </span>
        )}
      </div>

      <div className="page-body">
        <ErrorBanner error={error} />

        {mode === "json" ? (
          <JsonMode
            value={jsonText}
            onChange={setJsonText}
            error={jsonError}
            readOnly={readOnly}
          />
        ) : (
          <FormMode
            tool={tool}
            updateField={updateField}
            readOnly={readOnly}
            isNew={isNew}
          />
        )}
      </div>
    </>
  );
}

// ─── Form mode ───────────────────────────────────────────────────────
function FormMode({ tool, updateField, readOnly, isNew }) {
  return (
    <>
      <IdentityCard
        tool={tool}
        updateField={updateField}
        readOnly={readOnly}
        isNew={isNew}
      />
      {tool.kind === "server" && (
        <RequestCard
          request={tool.request}
          updateField={updateField}
          readOnly={readOnly}
        />
      )}
      {(tool.kind === "client" || tool.kind === "builtin") && (
        <ClientParamsCard
          tool={tool}
          updateField={updateField}
          readOnly={readOnly}
        />
      )}
      {tool.kind === "builtin" && (
        <BuiltinHandlerCard
          handler={tool.handler || {}}
          updateField={updateField}
          readOnly={readOnly}
        />
      )}
      <BehaviorCard tool={tool} updateField={updateField} readOnly={readOnly} />
      <RuntimeVariablesCard
        variables={tool.runtime_variables || { declared: [], defaults: {} }}
        updateField={updateField}
        readOnly={readOnly}
      />
      <ResultBindingsCard
        bindings={tool.result_bindings || []}
        updateField={updateField}
        readOnly={readOnly}
      />
      <StubResponsesCard
        stubs={tool.stub_responses || []}
        updateField={updateField}
        readOnly={readOnly}
      />
    </>
  );
}

// ─── Identity card (id, name, description, category, kind) ─────────
function IdentityCard({ tool, updateField, readOnly, isNew }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Identity</h3>
      </div>
      <div className="form-field">
        <label>ID (slug)</label>
        <input
          type="text"
          value={tool.id || tool._id || ""}
          onChange={(e) => updateField(["id"], e.target.value)}
          readOnly={!isNew || readOnly}
          placeholder="e.g. schedule_appointment"
          style={!isNew ? { background: "#f6f6f6" } : {}}
        />
        {!isNew && (
          <div className="help">The id is immutable once created.</div>
        )}
      </div>
      <div className="form-field">
        <label>Name</label>
        <input
          type="text"
          value={tool.name || ""}
          onChange={(e) => updateField(["name"], e.target.value)}
          readOnly={readOnly}
          placeholder="Display name"
        />
      </div>
      <div className="form-field">
        <label>Description</label>
        <textarea
          rows={3}
          value={tool.description || ""}
          onChange={(e) => updateField(["description"], e.target.value)}
          readOnly={readOnly}
          placeholder="Describe when the LLM should call this tool"
        />
      </div>
      <div className="field-row">
        <div className="form-field" style={{ flex: 1 }}>
          <label>Category</label>
          <input
            type="text"
            value={tool.category || ""}
            onChange={(e) => updateField(["category"], e.target.value)}
            readOnly={readOnly}
            placeholder="Scheduling, Clinical, Orchestration, ..."
          />
        </div>
        <div className="form-field" style={{ width: 150 }}>
          <label>Timeout (seconds)</label>
          <input
            type="number"
            min={1}
            max={600}
            value={tool.timeout_seconds ?? 20}
            onChange={(e) =>
              updateField(["timeout_seconds"], parseInt(e.target.value, 10) || 0)
            }
            readOnly={readOnly}
          />
        </div>
      </div>
      <div className="form-field">
        <label>Tags (comma separated)</label>
        <input
          type="text"
          value={(tool.tags || []).join(", ")}
          onChange={(e) =>
            updateField(
              ["tags"],
              e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            )
          }
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}

// ─── Request card (server only) ─────────────────────────────────────
function RequestCard({ request, updateField, readOnly }) {
  const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
  return (
    <div className="card">
      <div className="card-header">
        <h3>Request</h3>
      </div>
      <div className="field-row">
        <div className="form-field" style={{ width: 130 }}>
          <label>Method</label>
          <select
            value={request.method || "GET"}
            onChange={(e) => updateField(["request", "method"], e.target.value)}
            disabled={readOnly}
          >
            {METHODS.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className="form-field" style={{ flex: 1 }}>
          <label>URL</label>
          <input
            type="text"
            value={request.url || ""}
            onChange={(e) => updateField(["request", "url"], e.target.value)}
            readOnly={readOnly}
            placeholder="https://api.example.com/v1/resource/{id}"
          />
          <div className="help">
            Use <span className="mono">{"{param}"}</span> for path params and{" "}
            <span className="mono">{"{{env.VAR}}"}</span> or{" "}
            <span className="mono">{"{{runtime.var}}"}</span> for substitutions.
          </div>
        </div>
      </div>
      <div className="field-row">
        <div className="form-field" style={{ flex: 1 }}>
          <label>Content-Type</label>
          <input
            type="text"
            value={request.content_type || ""}
            onChange={(e) =>
              updateField(["request", "content_type"], e.target.value)
            }
            readOnly={readOnly}
          />
        </div>
        <div className="form-field" style={{ flex: 1 }}>
          <label>Auth ref</label>
          <input
            type="text"
            value={request.auth_ref || ""}
            onChange={(e) =>
              updateField(["request", "auth_ref"], e.target.value || null)
            }
            readOnly={readOnly}
            placeholder="workspace auth connection id"
          />
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <h4 className="section-label-small">Headers</h4>
        <HeadersEditor
          headers={request.headers || []}
          onChange={(next) => updateField(["request", "headers"], next)}
          readOnly={readOnly}
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <h4 className="section-label-small">Path parameters</h4>
        <div className="muted small mb-8">
          Wrap path segments in <span className="mono">{"{braces}"}</span> in
          the URL, then map each one here.
        </div>
        <ParamTable
          params={request.path_params || []}
          onChange={(next) => updateField(["request", "path_params"], next)}
          readOnly={readOnly}
          kind="path"
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <h4 className="section-label-small">Query parameters</h4>
        <ParamTable
          params={request.query_params || []}
          onChange={(next) => updateField(["request", "query_params"], next)}
          readOnly={readOnly}
          kind="query"
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <h4 className="section-label-small">Body parameters</h4>
        <ParamTable
          params={request.body_params || []}
          onChange={(next) => updateField(["request", "body_params"], next)}
          readOnly={readOnly}
          kind="body"
        />
      </div>
    </div>
  );
}

// ─── Client / builtin parameters card ───────────────────────────────
function ClientParamsCard({ tool, updateField, readOnly }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Parameters</h3>
      </div>
      {tool.kind === "client" && (
        <div className="form-field">
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={!!tool.awaits_reply}
              onChange={(e) => updateField(["awaits_reply"], e.target.checked)}
              disabled={readOnly}
            />
            Awaits reply (agent waits for the client to call back before continuing)
          </label>
        </div>
      )}
      <ParamTable
        params={tool.parameters || []}
        onChange={(next) => updateField(["parameters"], next)}
        readOnly={readOnly}
        kind="flat"
      />
    </div>
  );
}

// ─── Builtin handler card ───────────────────────────────────────────
function BuiltinHandlerCard({ handler, updateField, readOnly }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>In-process handler</h3>
      </div>
      <div className="field-row">
        <div className="form-field" style={{ flex: 1 }}>
          <label>Module</label>
          <input
            type="text"
            value={handler.module || ""}
            onChange={(e) =>
              updateField(["handler", "module"], e.target.value)
            }
            readOnly={readOnly}
            placeholder="orchestrator, protocol-store, session, ..."
          />
        </div>
        <div className="form-field" style={{ flex: 1 }}>
          <label>Function</label>
          <input
            type="text"
            value={handler.function || ""}
            onChange={(e) =>
              updateField(["handler", "function"], e.target.value)
            }
            readOnly={readOnly}
            placeholder="routeToAgent, getTriageProtocol, ..."
          />
        </div>
      </div>
    </div>
  );
}

// ─── Behavior card (barge-in, narration, dispatch, on-error) ────────
function BehaviorCard({ tool, updateField, readOnly }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Behavior</h3>
      </div>
      <div className="form-field">
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={!!tool.blocks_barge_in}
            onChange={(e) =>
              updateField(["blocks_barge_in"], e.target.checked)
            }
            disabled={readOnly}
          />
          Blocks barge-in (patient cannot interrupt while tool is running)
        </label>
      </div>
      <div className="field-row">
        <div className="form-field" style={{ flex: 1 }}>
          <label>Pre-call narration</label>
          <select
            value={tool.pre_call_narration || "auto"}
            onChange={(e) =>
              updateField(["pre_call_narration"], e.target.value)
            }
            disabled={readOnly}
          >
            {NARRATION_OPTIONS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
          <div className="help">
            Whether the agent speaks before invoking the tool.
          </div>
        </div>
        <div className="form-field" style={{ flex: 1 }}>
          <label>Dispatch</label>
          <select
            value={tool.dispatch || "immediate"}
            onChange={(e) => updateField(["dispatch"], e.target.value)}
            disabled={readOnly}
          >
            {DISPATCH_OPTIONS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </div>
        <div className="form-field" style={{ flex: 1 }}>
          <label>On error</label>
          <select
            value={tool.on_error || "auto"}
            onChange={(e) => updateField(["on_error"], e.target.value)}
            disabled={readOnly}
          >
            {ON_ERROR_OPTIONS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="field-row">
        <div className="form-field" style={{ flex: 1 }}>
          <label>Progress sound</label>
          <input
            type="text"
            value={tool.progress_sound || ""}
            onChange={(e) =>
              updateField(["progress_sound"], e.target.value || null)
            }
            readOnly={readOnly}
            placeholder="sound ref (or leave empty)"
          />
        </div>
        <div className="form-field" style={{ flex: 1 }}>
          <label>Progress sound mode</label>
          <select
            value={tool.progress_sound_mode || "auto"}
            onChange={(e) =>
              updateField(["progress_sound_mode"], e.target.value)
            }
            disabled={readOnly}
          >
            {PROGRESS_SOUND_MODES.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

// ─── Parameter table sub-editor ─────────────────────────────────────
function ParamTable({ params, onChange, readOnly, kind }) {
  const addRow = () => {
    onChange([
      ...params,
      {
        name: "",
        type: "string",
        required: true,
        source: "llm",
        description: "",
      },
    ]);
  };
  const removeRow = (i) => {
    const next = params.slice();
    next.splice(i, 1);
    onChange(next);
  };
  const updateRow = (i, patch) => {
    const next = params.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };

  if (params.length === 0 && readOnly) {
    return <div className="muted small">No parameters defined.</div>;
  }

  return (
    <div className="param-table">
      {params.length > 0 && (
        <table className="table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th style={{ width: "22%" }}>Name</th>
              <th style={{ width: "12%" }}>Type</th>
              <th style={{ width: "14%" }}>Source</th>
              <th>Details</th>
              <th style={{ width: "10%" }}>Required</th>
              {!readOnly && <th style={{ width: 36 }}></th>}
            </tr>
          </thead>
          <tbody>
            {params.map((p, i) => (
              <tr key={i}>
                <td>
                  <input
                    type="text"
                    value={p.name || ""}
                    onChange={(e) => updateRow(i, { name: e.target.value })}
                    readOnly={readOnly}
                    className="input-compact mono"
                  />
                </td>
                <td>
                  <select
                    value={p.type || "string"}
                    onChange={(e) => updateRow(i, { type: e.target.value })}
                    disabled={readOnly}
                    className="input-compact"
                  >
                    {PARAM_TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={p.source || "llm"}
                    onChange={(e) => updateRow(i, { source: e.target.value })}
                    disabled={readOnly}
                    className="input-compact"
                  >
                    {PARAM_SOURCES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {p.source === "runtime_variable" ? (
                    <input
                      type="text"
                      value={p.variable_ref || ""}
                      onChange={(e) =>
                        updateRow(i, { variable_ref: e.target.value })
                      }
                      readOnly={readOnly}
                      className="input-compact mono"
                      placeholder="runtime var name"
                    />
                  ) : p.source === "constant" ? (
                    <input
                      type="text"
                      value={p.constant_value || ""}
                      onChange={(e) =>
                        updateRow(i, { constant_value: e.target.value })
                      }
                      readOnly={readOnly}
                      className="input-compact mono"
                      placeholder="constant value"
                    />
                  ) : (
                    <input
                      type="text"
                      value={p.description || ""}
                      onChange={(e) =>
                        updateRow(i, { description: e.target.value })
                      }
                      readOnly={readOnly}
                      className="input-compact"
                      placeholder="description for the LLM"
                    />
                  )}
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={!!p.required}
                    onChange={(e) =>
                      updateRow(i, { required: e.target.checked })
                    }
                    disabled={readOnly}
                  />
                </td>
                {!readOnly && (
                  <td>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => removeRow(i)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!readOnly && (
        <button type="button" className="btn sm" onClick={addRow}>
          + Add param
        </button>
      )}
    </div>
  );
}

// ─── Headers sub-editor ─────────────────────────────────────────────
function HeadersEditor({ headers, onChange, readOnly }) {
  const addRow = () => onChange([...headers, { name: "", value: "" }]);
  const removeRow = (i) => {
    const next = headers.slice();
    next.splice(i, 1);
    onChange(next);
  };
  const updateRow = (i, patch) => {
    const next = headers.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  return (
    <div>
      {headers.length > 0 && (
        <table className="table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th style={{ width: "30%" }}>Name</th>
              <th>Value</th>
              {!readOnly && <th style={{ width: 36 }}></th>}
            </tr>
          </thead>
          <tbody>
            {headers.map((h, i) => (
              <tr key={i}>
                <td>
                  <input
                    type="text"
                    value={h.name || ""}
                    onChange={(e) => updateRow(i, { name: e.target.value })}
                    readOnly={readOnly}
                    className="input-compact mono"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={h.value || ""}
                    onChange={(e) => updateRow(i, { value: e.target.value })}
                    readOnly={readOnly}
                    className="input-compact mono"
                  />
                </td>
                {!readOnly && (
                  <td>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => removeRow(i)}
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!readOnly && (
        <button type="button" className="btn sm" onClick={addRow}>
          + Add header
        </button>
      )}
    </div>
  );
}

// ─── Runtime variables ───────────────────────────────────────────────
function RuntimeVariablesCard({ variables, updateField, readOnly }) {
  const declared = variables.declared || [];
  const defaults = variables.defaults || {};

  const addVar = () => {
    const next = [...declared, ""];
    updateField(["runtime_variables", "declared"], next);
  };
  const removeVar = (i) => {
    const name = declared[i];
    const next = declared.slice();
    next.splice(i, 1);
    updateField(["runtime_variables", "declared"], next);
    if (name && defaults[name] !== undefined) {
      const nextDefaults = { ...defaults };
      delete nextDefaults[name];
      updateField(["runtime_variables", "defaults"], nextDefaults);
    }
  };
  const renameVar = (i, name) => {
    const next = declared.slice();
    const old = next[i];
    next[i] = name;
    updateField(["runtime_variables", "declared"], next);
    if (old && defaults[old] !== undefined) {
      const nextDefaults = { ...defaults };
      nextDefaults[name] = nextDefaults[old];
      delete nextDefaults[old];
      updateField(["runtime_variables", "defaults"], nextDefaults);
    }
  };
  const setDefault = (name, value) => {
    updateField(["runtime_variables", "defaults"], {
      ...defaults,
      [name]: value,
    });
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3>Runtime variables</h3>
      </div>
      <div className="muted small mb-16">
        Variables declared here can be referenced as{" "}
        <span className="mono">{"{{runtime.name}}"}</span> in the URL,
        headers, or parameter values. They resolve at conversation start from
        the caller context.
      </div>
      {declared.length > 0 && (
        <table className="table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Default</th>
              {!readOnly && <th style={{ width: 36 }}></th>}
            </tr>
          </thead>
          <tbody>
            {declared.map((name, i) => (
              <tr key={i}>
                <td>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => renameVar(i, e.target.value)}
                    readOnly={readOnly}
                    className="input-compact mono"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={defaults[name] ?? ""}
                    onChange={(e) => setDefault(name, e.target.value)}
                    readOnly={readOnly}
                    className="input-compact"
                  />
                </td>
                {!readOnly && (
                  <td>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => removeVar(i)}
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!readOnly && (
        <button type="button" className="btn sm" onClick={addVar}>
          + Add variable
        </button>
      )}
    </div>
  );
}

// ─── Result bindings ─────────────────────────────────────────────────
function ResultBindingsCard({ bindings, updateField, readOnly }) {
  const addRow = () =>
    updateField(
      ["result_bindings"],
      [...bindings, { from: "$.field", to_variable: "" }]
    );
  const removeRow = (i) => {
    const next = bindings.slice();
    next.splice(i, 1);
    updateField(["result_bindings"], next);
  };
  const updateRow = (i, patch) => {
    const next = bindings.slice();
    next[i] = { ...next[i], ...patch };
    updateField(["result_bindings"], next);
  };
  return (
    <div className="card">
      <div className="card-header">
        <h3>Result bindings</h3>
      </div>
      <div className="muted small mb-16">
        When the tool returns a response, extract fields into runtime
        variables so later tool calls or prompts can reference them.
      </div>
      {bindings.length > 0 && (
        <table className="table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th>From (JSONPath)</th>
              <th>To variable</th>
              {!readOnly && <th style={{ width: 36 }}></th>}
            </tr>
          </thead>
          <tbody>
            {bindings.map((b, i) => (
              <tr key={i}>
                <td>
                  <input
                    type="text"
                    value={b.from || ""}
                    onChange={(e) => updateRow(i, { from: e.target.value })}
                    readOnly={readOnly}
                    className="input-compact mono"
                    placeholder="$.data.id"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={b.to_variable || ""}
                    onChange={(e) =>
                      updateRow(i, { to_variable: e.target.value })
                    }
                    readOnly={readOnly}
                    className="input-compact mono"
                    placeholder="last_booking_id"
                  />
                </td>
                {!readOnly && (
                  <td>
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => removeRow(i)}
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!readOnly && (
        <button type="button" className="btn sm" onClick={addRow}>
          + Add binding
        </button>
      )}
    </div>
  );
}

// ─── Stub responses ──────────────────────────────────────────────────
function StubResponsesCard({ stubs, updateField, readOnly }) {
  const addRow = () =>
    updateField(
      ["stub_responses"],
      [
        ...stubs,
        {
          name: `stub-${stubs.length + 1}`,
          when: { kind: "always" },
          then: { status: 200, body: {} },
        },
      ]
    );
  const removeRow = (i) => {
    const next = stubs.slice();
    next.splice(i, 1);
    updateField(["stub_responses"], next);
  };
  const updateRow = (i, patch) => {
    const next = stubs.slice();
    next[i] = { ...next[i], ...patch };
    updateField(["stub_responses"], next);
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3>Stub responses</h3>
      </div>
      <div className="muted small mb-16">
        Mock responses for test simulations. Conditions are evaluated
        top-to-bottom and the first match is returned.
      </div>
      {stubs.length === 0 ? (
        <div className="muted small mb-16">No stub responses defined.</div>
      ) : (
        stubs.map((s, i) => (
          <div
            key={i}
            className="card"
            style={{
              padding: 12,
              marginBottom: 10,
              background: "var(--color-surface-muted)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <input
                type="text"
                value={s.name || ""}
                onChange={(e) => updateRow(i, { name: e.target.value })}
                readOnly={readOnly}
                className="input-compact"
                style={{ fontWeight: 600, maxWidth: 260 }}
                placeholder="stub name"
              />
              {!readOnly && (
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => removeRow(i)}
                >
                  ×
                </button>
              )}
            </div>
            <div className="field-row">
              <div className="form-field" style={{ flex: 1 }}>
                <label>When</label>
                <select
                  value={s.when?.kind || "always"}
                  onChange={(e) =>
                    updateRow(i, { when: { ...s.when, kind: e.target.value } })
                  }
                  disabled={readOnly}
                >
                  <option value="always">always</option>
                  <option value="arg_match">arg_match</option>
                </select>
              </div>
              {s.when?.kind === "arg_match" && (
                <>
                  <div className="form-field" style={{ flex: 1 }}>
                    <label>Arg name</label>
                    <input
                      type="text"
                      value={s.when?.arg || ""}
                      onChange={(e) =>
                        updateRow(i, {
                          when: { ...s.when, arg: e.target.value },
                        })
                      }
                      readOnly={readOnly}
                      className="input-compact mono"
                    />
                  </div>
                  <div className="form-field" style={{ flex: 1 }}>
                    <label>Equals</label>
                    <input
                      type="text"
                      value={s.when?.equals || ""}
                      onChange={(e) =>
                        updateRow(i, {
                          when: { ...s.when, equals: e.target.value },
                        })
                      }
                      readOnly={readOnly}
                      className="input-compact mono"
                    />
                  </div>
                </>
              )}
              <div className="form-field" style={{ width: 100 }}>
                <label>Status</label>
                <input
                  type="number"
                  min={100}
                  max={599}
                  value={s.then?.status ?? 200}
                  onChange={(e) =>
                    updateRow(i, {
                      then: {
                        ...s.then,
                        status: parseInt(e.target.value, 10) || 200,
                      },
                    })
                  }
                  readOnly={readOnly}
                  className="input-compact"
                />
              </div>
            </div>
            <div className="form-field">
              <label>Response body (JSON)</label>
              <textarea
                rows={4}
                value={JSON.stringify(s.then?.body ?? {}, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    updateRow(i, { then: { ...s.then, body: parsed } });
                  } catch {
                    // keep previous body while user is typing invalid JSON
                  }
                }}
                readOnly={readOnly}
                className="mono"
                style={{ fontSize: 12 }}
              />
            </div>
          </div>
        ))
      )}
      {!readOnly && (
        <button type="button" className="btn sm" onClick={addRow}>
          + Add stub
        </button>
      )}
    </div>
  );
}

// ─── JSON mode editor ────────────────────────────────────────────────
function JsonMode({ value, onChange, error, readOnly }) {
  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(value);
    } catch {
      // ignore
    }
  };

  // Live validate
  const isValid = useMemo(() => {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }, [value]);

  const lineCount = useMemo(() => (value || "").split("\n").length, [value]);

  return (
    <div className="card">
      <div className="card-header">
        <h3>JSON</h3>
        <div
          className="btn-row"
          style={{ marginLeft: "auto" }}
        >
          <span className={`small mono ${isValid ? "muted" : "error-text"}`}>
            {isValid ? "valid" : "invalid JSON"}
          </span>
          <button type="button" className="btn sm" onClick={handleCopy}>
            Copy
          </button>
        </div>
      </div>
      <div className="json-editor-shell">
        <div className="json-line-numbers">
          {Array.from({ length: lineCount }).map((_, i) => (
            <div key={i}>{i + 1}</div>
          ))}
        </div>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          spellCheck={false}
          className="json-editor-textarea mono"
        />
      </div>
      {error && (
        <div
          className="error-banner"
          style={{ marginTop: 12 }}
        >
          <strong>Parse error:</strong> {error}
        </div>
      )}
    </div>
  );
}

// ─── Deep setter ────────────────────────────────────────────────────
function setPath(obj, path, value) {
  if (path.length === 0) return value;
  const [head, ...tail] = path;
  const next = Array.isArray(obj) ? obj.slice() : { ...(obj || {}) };
  next[head] = setPath(obj?.[head], tail, value);
  return next;
}
